import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plane,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  User,
  Users,
  MapPin,
  Calendar,
  DollarSign,
  FileCheck,
  Building2,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NewTravelRequest = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Wizard Form Data (Strictly held in state; NEVER auto-saved to API until explicit Submit)
  const [formData, setFormData] = useState({
    purpose: '',
    travel_type: 'flight',
    travel_category: 'Domestic',
    is_international: false,
    is_emergency: false,
    project: 'Internal',
    client: 'N/A',

    journey_details: [
      {
        from_city: '',
        to_city: '',
        departure_date: '',
        return_date: '',
        preferred_time: 'Morning (06:00 - 12:00)',
      },
    ],

    passengers: [
      {
        name: '',
        age: '',
        gender: 'Male',
        id_type: 'Aadhaar Card',
        id_number: '',
        is_guest: false,
      },
    ],

    booking_preferences: {
      flight_class: 'economy',
      train_class: '3ac',
      bus_class: 'ac_sleeper',
      cab_type: 'sedan',
      hotel_rating_preferred: 4,
    },

    cost_center: 'CC-GEN-01',
    gl_account: 'GL-4890',
    estimated_budget: 15000,
    remarks: '',
  });

  const updateJourneyField = (idx, field, value) => {
    const nextLegs = [...formData.journey_details];
    nextLegs[idx][field] = value;
    setFormData({ ...formData, journey_details: nextLegs });
  };

  const updatePassengerField = (idx, field, value) => {
    const nextPax = [...formData.passengers];
    nextPax[idx][field] = value;
    setFormData({ ...formData, passengers: nextPax });
  };

  const addPassenger = () => {
    setFormData({
      ...formData,
      passengers: [
        ...formData.passengers,
        { name: '', age: '', gender: 'Male', id_type: 'Aadhaar Card', id_number: '', is_guest: false },
      ],
    });
  };

  const removePassenger = (idx) => {
    if (formData.passengers.length === 1) return;
    setFormData({
      ...formData,
      passengers: formData.passengers.filter((_, i) => i !== idx),
    });
  };

  // Validate step navigation
  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!formData.purpose.trim()) {
        toast.error('Please specify Travel Purpose.');
        return;
      }
      const leg = formData.journey_details[0];
      if (!leg.from_city.trim() || !leg.to_city.trim()) {
        toast.error('Please specify From and To cities.');
        return;
      }
      if (!leg.departure_date) {
        toast.error('Please select Departure Date.');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.passengers[0].name.trim()) {
        toast.error('Please enter details for at least Passenger 1.');
        return;
      }
    } else if (currentStep === 4) {
      if (!formData.estimated_budget || formData.estimated_budget <= 0) {
        toast.error('Please enter a valid estimated budget.');
        return;
      }
    }

    // Move to next step (STRICTLY NO API SAVE HERE)
    setCurrentStep((prev) => Math.min(6, prev + 1));
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  // Final submission (ONLY here API save executes)
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        status: 'submitted',
      };

      const { data } = await axios.post(`${API_URL}/travel-desk/requests`, payload, {
        withCredentials: true,
      });

      toast.success('Travel Request submitted successfully!');
      navigate(`/travel/request/${data.request._id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit travel request');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { num: 1, title: 'Travel Details' },
    { num: 2, title: 'Passengers' },
    { num: 3, title: 'Preferences' },
    { num: 4, title: 'Cost Centre' },
    { num: 5, title: 'Review' },
    { num: 6, title: 'Submit' },
  ];

  return (
    <div className="p-6 space-y-6 max-w-[1200px] mx-auto font-sans">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
              <Plane className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight">New Corporate Travel Request</h1>
              <p className="text-xs text-blue-200">
                Complete the 6-step requisition wizard to request travel, accommodation, or cabs.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate('/travel/requests')}
          className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl"
        >
          &larr; Cancel &amp; Back
        </button>
      </div>

      {/* Stepper Bar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between">
          {steps.map((s, idx) => {
            const isActive = currentStep === s.num;
            const isCompleted = currentStep > s.num;
            return (
              <React.Fragment key={s.num}>
                <div
                  onClick={() => {
                    if (s.num < currentStep) setCurrentStep(s.num);
                  }}
                  className={`flex items-center gap-2 cursor-pointer ${
                    isActive ? 'text-blue-600 font-bold' : isCompleted ? 'text-emerald-600 font-bold' : 'text-slate-400'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : isCompleted
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="text-xs hidden sm:inline">{s.title}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${s.num < currentStep ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Container Card */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 min-h-[420px]">
        {/* STEP 1: TRAVEL DETAILS */}
        {currentStep === 1 && (
          <div className="space-y-5">
            <h3 className="font-bold text-slate-900 text-base border-b border-slate-200 pb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" /> Step 1: Travel &amp; Journey Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Travel Purpose *</label>
                <input
                  type="text"
                  placeholder="e.g. Client Onboarding Meeting"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium outline-none focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Primary Travel Mode</label>
                <select
                  value={formData.travel_type}
                  onChange={(e) => setFormData({ ...formData, travel_type: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
                >
                  <option value="flight">Flight</option>
                  <option value="train">Train</option>
                  <option value="bus">Bus</option>
                  <option value="cab">Cab / Taxi</option>
                  <option value="hotel">Hotel Only</option>
                  <option value="multi_city">Multi-City</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Travel Category</label>
                <select
                  value={formData.travel_category}
                  onChange={(e) => setFormData({ ...formData, travel_category: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
                >
                  <option value="Domestic">Domestic</option>
                  <option value="International">International</option>
                </select>
              </div>
            </div>

            <div className="flex gap-6 pt-2">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_international}
                  onChange={(e) => setFormData({ ...formData, is_international: e.target.checked })}
                  className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                International Travel
              </label>

              <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_emergency}
                  onChange={(e) => setFormData({ ...formData, is_emergency: e.target.checked })}
                  className="rounded text-amber-600 focus:ring-amber-500 w-4 h-4"
                />
                Emergency / Urgent Travel
              </label>
            </div>

            <div className="pt-3 space-y-3">
              <h4 className="font-bold text-xs uppercase text-slate-500 tracking-wider">Route &amp; Dates</h4>
              {formData.journey_details.map((leg, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">From City *</label>
                    <input
                      type="text"
                      placeholder="Origin (e.g. Mumbai)"
                      value={leg.from_city}
                      onChange={(e) => updateJourneyField(idx, 'from_city', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">To City *</label>
                    <input
                      type="text"
                      placeholder="Destination (e.g. Bengaluru)"
                      value={leg.to_city}
                      onChange={(e) => updateJourneyField(idx, 'to_city', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Departure Date *</label>
                    <input
                      type="date"
                      value={leg.departure_date}
                      onChange={(e) => updateJourneyField(idx, 'departure_date', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Return Date</label>
                    <input
                      type="date"
                      value={leg.return_date}
                      onChange={(e) => updateJourneyField(idx, 'return_date', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2: PASSENGERS */}
        {currentStep === 2 && (
          <div className="space-y-5">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" /> Step 2: Passenger Manifest
              </h3>
              <button
                onClick={addPassenger}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 font-bold text-xs rounded-xl"
              >
                + Add Passenger
              </button>
            </div>

            <div className="space-y-4">
              {formData.passengers.map((pax, idx) => (
                <div key={idx} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xs text-slate-700">Passenger #{idx + 1}</span>
                    {formData.passengers.length > 1 && (
                      <button
                        onClick={() => removePassenger(idx)}
                        className="text-xs text-rose-600 font-bold hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Full Name *</label>
                      <input
                        type="text"
                        placeholder="As on Government ID"
                        value={pax.name}
                        onChange={(e) => updatePassengerField(idx, 'name', e.target.value)}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Age</label>
                      <input
                        type="number"
                        placeholder="e.g. 32"
                        value={pax.age}
                        onChange={(e) => updatePassengerField(idx, 'age', e.target.value)}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Gender</label>
                      <select
                        value={pax.gender}
                        onChange={(e) => updatePassengerField(idx, 'gender', e.target.value)}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">ID Number (Aadhaar / Passport)</label>
                      <input
                        type="text"
                        placeholder="ID details"
                        value={pax.id_number}
                        onChange={(e) => updatePassengerField(idx, 'id_number', e.target.value)}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 3: PREFERENCES */}
        {currentStep === 3 && (
          <div className="space-y-5">
            <h3 className="font-bold text-slate-900 text-base border-b border-slate-200 pb-3 flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-blue-600" /> Step 3: Booking Preferences &amp; Class Entitlement
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Preferred Flight Class</label>
                <select
                  value={formData.booking_preferences.flight_class}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      booking_preferences: { ...formData.booking_preferences, flight_class: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold"
                >
                  <option value="economy">Economy Class</option>
                  <option value="premium_economy">Premium Economy</option>
                  <option value="business">Business Class</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Preferred Train Tier</label>
                <select
                  value={formData.booking_preferences.train_class}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      booking_preferences: { ...formData.booking_preferences, train_class: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold"
                >
                  <option value="3ac">3 AC Tier</option>
                  <option value="2ac">2 AC Tier</option>
                  <option value="1ac">1 AC Tier</option>
                  <option value="sleeper">Sleeper Class</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Cab Type Preference</label>
                <select
                  value={formData.booking_preferences.cab_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      booking_preferences: { ...formData.booking_preferences, cab_type: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold"
                >
                  <option value="sedan">Sedan (Dzire / Etios)</option>
                  <option value="suv">SUV (Innova / Ertiga)</option>
                  <option value="hatchback">Hatchback (WagonR)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Hotel Star Rating Preferred</label>
                <select
                  value={formData.booking_preferences.hotel_rating_preferred}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      booking_preferences: { ...formData.booking_preferences, hotel_rating_preferred: Number(e.target.value) },
                    })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold"
                >
                  <option value={3}>3 Star Hotel</option>
                  <option value={4}>4 Star Hotel</option>
                  <option value={5}>5 Star Luxury Hotel</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: COST CENTRE */}
        {currentStep === 4 && (
          <div className="space-y-5">
            <h3 className="font-bold text-slate-900 text-base border-b border-slate-200 pb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" /> Step 4: Cost Centre &amp; Budget Estimate
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Cost Center Code</label>
                <input
                  type="text"
                  value={formData.cost_center}
                  onChange={(e) => setFormData({ ...formData, cost_center: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">GL Account Number</label>
                <input
                  type="text"
                  value={formData.gl_account}
                  onChange={(e) => setFormData({ ...formData, gl_account: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Estimated Total Budget (₹) *</label>
                <input
                  type="number"
                  value={formData.estimated_budget}
                  onChange={(e) => setFormData({ ...formData, estimated_budget: Number(e.target.value) })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-bold text-emerald-700"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Special Remarks / Justification</label>
              <textarea
                rows={3}
                placeholder="Add any specific guidelines for travel team..."
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium outline-none"
              />
            </div>
          </div>
        )}

        {/* STEP 5: REVIEW */}
        {currentStep === 5 && (
          <div className="space-y-5">
            <h3 className="font-bold text-slate-900 text-base border-b border-slate-200 pb-3 flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-blue-600" /> Step 5: Review Travel Requisition
            </h3>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div><b>Purpose:</b> {formData.purpose}</div>
                <div><b>Travel Mode:</b> <span className="capitalize">{formData.travel_type}</span> ({formData.travel_category})</div>
                <div><b>Route:</b> {formData.journey_details[0].from_city} &rarr; {formData.journey_details[0].to_city}</div>
                <div><b>Departure:</b> {formData.journey_details[0].departure_date || 'N/A'}</div>
                <div><b>Passengers:</b> {formData.passengers.map(p => p.name).filter(Boolean).join(', ')}</div>
                <div><b>Est. Budget:</b> ₹ {(formData.estimated_budget || 0).toLocaleString('en-IN')}</div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 6: SUBMIT */}
        {currentStep === 6 && (
          <div className="space-y-5 text-center py-6">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto" />
            <h3 className="text-lg font-black text-slate-900">Ready to Submit Travel Request</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Clicking <b>Submit Request</b> will register your requisition into the Travel Desk approval matrix. Your reporting manager will be notified.
            </p>

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-sm rounded-xl shadow-lg transition-all"
            >
              {submitting ? 'Submitting Request...' : 'Submit Travel Request Now'}
            </button>
          </div>
        )}
      </div>

      {/* Navigation Actions Footer (Next moves step ONLY, NEVER saves) */}
      <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <button
          onClick={handlePrevStep}
          disabled={currentStep === 1}
          className="px-4 py-2 bg-slate-100 disabled:opacity-40 text-slate-700 font-bold text-xs rounded-xl flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" /> Previous Step
        </button>

        {currentStep < 6 && (
          <button
            onClick={handleNextStep}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-md flex items-center gap-1"
          >
            Next Step <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default NewTravelRequest;
